import React, { useState } from 'react';
import { Tooltip, IconButton, InputAdornment, TextField } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import CancelScheduleSendOutlinedIcon from '@mui/icons-material/CancelScheduleSendOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CustomButton from '../../../components/buttons/CustomButton';
import { USER_ROLES, HEALTH_PROFESSIONAL_LABEL } from '../../../constants/userRoles';
import personInitials from './personInitials';
import { ROLE_ICONS, roleLabel } from './roleVisuals';
import styles from './TeamList.module.css';

const STATUS_ORDER = { active: 0, pending: 1, revoked: 2 };

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR');
};

function StatusTag({ member }) {
  if (member.status === 'pending') {
    const until = formatDate(member.expiresAt);
    const expired = member.expiresAt && new Date(String(member.expiresAt).replace(' ', 'T')) < new Date();
    if (expired) {
      return <span className={`${styles.status} ${styles.revoked}`}>Convite vencido em {until}</span>;
    }
    return (
      <span className={`${styles.status} ${styles.pending}`}>
        Convite enviado{until ? ` · vale até ${until}` : ''}
      </span>
    );
  }
  if (member.status === 'revoked') {
    return <span className={`${styles.status} ${styles.revoked}`}>Sem acesso</span>;
  }
  return <span className={`${styles.status} ${styles.active}`}>Ativo</span>;
}

/** Selo da marcação "Profissional de saúde" (ou do pedido dela), ao lado do papel. */
function HealthTag({ member }) {
  if (member.type === 'invitation') {
    if (!member.healthProfessionalRequested) return null;
    return (
      <span className={`${styles.health} ${styles.healthPending}`}>
        <MedicalServicesOutlinedIcon className={styles.healthIcon} />Atendimentos pedidos no convite
      </span>
    );
  }
  if (member.isHealthProfessional) {
    return (
      <span className={`${styles.health} ${styles.healthGranted}`}>
        <MedicalServicesOutlinedIcon className={styles.healthIcon} />{HEALTH_PROFESSIONAL_LABEL}
      </span>
    );
  }
  if (member.healthProfessionalRequestedAt && member.status === 'active') {
    return (
      <span className={`${styles.health} ${styles.healthPending}`}>
        <MedicalServicesOutlinedIcon className={styles.healthIcon} />Pediu acesso aos atendimentos
      </span>
    );
  }
  return null;
}

/** Agenda ligada e alcance "Só os meus pacientes", abaixo da marcação. */
function ScheduleTag({ member, resources }) {
  if (member.type === 'invitation' || !member.isHealthProfessional) return null;
  const agenda = member.resourceId != null ? resources.find((r) => r.id === Number(member.resourceId)) : null;
  const own = member.scheduleScope === 'own';
  if (!agenda && !own) return null;
  return (
    <span className={styles.schedule}>
      <CalendarMonthOutlinedIcon className={styles.healthIcon} />
      {agenda ? agenda.label : 'Sem agenda'}{own ? ' · só os próprios pacientes' : ''}
    </span>
  );
}

/** Ação compacta da linha: ícone com tooltip (o span mantém o tooltip no botão desligado). */
function RowAction({ title, label, disabled, onClick, Icon }) {
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton size="small" className={styles.editButton} onClick={onClick} disabled={disabled} aria-label={label}>
          <Icon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}

/**
 * Pessoas da empresa: uma linha por pessoa (avatar, nome e e-mail, papel e marcação,
 * situação), com busca e o atalho de edição para quem pode editar. canEditSelf deixa
 * a própria pessoa abrir a sua linha (o proprietário liga a própria marcação por ali).
 * Linha "Sem acesso" ganha "Devolver acesso"; convite pendente ganha "Reenviar" e
 * "Cancelar" (ícones compactos, com o mesmo critério de permissão da edição).
 * Cancelar convite pede confirmação na própria linha, como "Remover acesso" no popup.
 */
function TeamList({
  members, currentUserId, searchTerm, onSearchChange, resources = [], canEdit, canEditSelf, editDeniedReason,
  busyKey, onEdit, onReactivate, onResendInvite, onCancelInvite,
}) {
  // Linha com a confirmação de "Cancelar convite" aberta
  const [confirmCancelKey, setConfirmCancelKey] = useState(null);
  const term = searchTerm.trim().toLowerCase();
  const visible = members
    .filter((m) => !term || (m.name || '').toLowerCase().includes(term) || (m.email || '').toLowerCase().includes(term))
    .sort((a, b) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3));

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <div>
          <h3 className={styles.title}>Pessoas · {members.length}</h3>
        </div>
        <TextField
          size="small"
          placeholder="Buscar por nome ou e-mail"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className={styles.search}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" sx={{ color: 'var(--color-text-light)' }} />
              </InputAdornment>
            ),
          }}
        />
      </header>

      <ul className={styles.list}>
        {visible.map((member) => {
          const isInvite = member.type === 'invitation';
          const isMe = !isInvite && member.id == currentUserId;
          const isOwner = member.role === USER_ROLES.COMPANY_OWNER;
          const RoleIcon = ROLE_ICONS[member.role];
          const editable = member.status === 'active' && !isInvite && (!isMe || canEditSelf);
          const allowed = editable && canEdit(member);
          const manageable = canEdit(member);
          const rowKey = `${member.type || 'user'}-${member.id ?? member.email}`;
          const busy = busyKey === rowKey;
          const who = member.name || member.email;

          return (
            <li
              key={rowKey}
              className={`${styles.row} ${member.status === 'revoked' ? styles.muted : ''}`}
            >
              <span className={`${styles.avatar} ${isInvite ? styles.avatarPending : ''}`}>
                {member.name ? personInitials(member.name) : '@'}
              </span>

              <div className={styles.identity}>
                <span className={styles.name}>
                  <span className={styles.nameText}>{member.name || 'Aguardando cadastro'}</span>
                  {isMe && <span className={styles.me}>Você</span>}
                </span>
                <span className={styles.email}>{member.email}</span>
              </div>

              <div className={styles.roleCell}>
                <span className={`${styles.role} ${isOwner ? styles.ownerRole : ''}`}>
                  {RoleIcon && <RoleIcon className={styles.roleIcon} />}
                  <span className={styles.roleText}>{roleLabel(member.role)}</span>
                </span>
                <HealthTag member={member} />
                <ScheduleTag member={member} resources={resources} />
              </div>

              <StatusTag member={member} />

              <span className={styles.actions}>
                {editable && (
                  <Tooltip title={allowed ? 'Editar' : editDeniedReason} arrow>
                    <span>
                      <IconButton
                        size="small"
                        className={styles.editButton}
                        onClick={() => onEdit(member)}
                        disabled={!allowed}
                        aria-label={`Editar ${member.name || member.email}`}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {member.status === 'revoked' && !isInvite && onReactivate && (
                  <RowAction
                    title={manageable ? 'Devolver acesso' : editDeniedReason}
                    label={`Devolver acesso a ${who}`}
                    disabled={!manageable || busy}
                    onClick={() => onReactivate(member)}
                    Icon={HowToRegOutlinedIcon}
                  />
                )}
                {isInvite && onResendInvite && (
                  <RowAction
                    title={manageable ? 'Reenviar convite' : editDeniedReason}
                    label={`Reenviar convite para ${who}`}
                    disabled={!manageable || busy}
                    onClick={() => onResendInvite(member)}
                    Icon={ForwardToInboxOutlinedIcon}
                  />
                )}
                {isInvite && onCancelInvite && (
                  <RowAction
                    title={manageable ? 'Cancelar convite' : editDeniedReason}
                    label={`Cancelar convite de ${who}`}
                    disabled={!manageable || busy}
                    onClick={() => setConfirmCancelKey(rowKey)}
                    Icon={CancelScheduleSendOutlinedIcon}
                  />
                )}
              </span>

              {isInvite && onCancelInvite && confirmCancelKey === rowKey && (
                <div className={styles.confirm}>
                  <span className={styles.confirmText}>
                    O convite para {member.email} deixa de valer na hora e a vaga fica livre. Dá para convidar de novo depois.
                  </span>
                  <div className={styles.confirmActions}>
                    <CustomButton label="Manter" variant="text" onClick={() => setConfirmCancelKey(null)} />
                    <CustomButton
                      label="Cancelar convite"
                      variant="contained"
                      style="error"
                      enabled={manageable && !busy}
                      onClick={() => {
                        setConfirmCancelKey(null);
                        onCancelInvite(member);
                      }}
                    />
                  </div>
                </div>
              )}
            </li>
          );
        })}
        {visible.length === 0 && (
          <li className={styles.empty}>Ninguém encontrado com essa busca.</li>
        )}
      </ul>
    </section>
  );
}

export default TeamList;
